import { useState, useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import { useAnalytics } from './useAnalytics';

const STORAGE_KEY = 'superbar_auto_expand_shown';
const LONG_HOVER_THRESHOLD_MS = 2000;

const hasExpandedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markAsExpanded = (): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
};

interface UseSuperbarHoverOptions {
  autoExpandEnabled?: boolean;
  autoExpandDelaySeconds?: number;
  onAutoExpand?: () => void;
}

interface UseSuperbarHoverReturn {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

type TimeoutId = ReturnType<typeof setTimeout>;

/**
 * Unified hook for Superbar hover behavior:
 * - Tracks hover analytics events
 * - Optionally triggers auto-expand after configurable delay (once per session)
 * - Desktop only for auto-expand
 */
export const useSuperbarHover = ({
  autoExpandEnabled = false,
  autoExpandDelaySeconds = 2,
  onAutoExpand,
}: UseSuperbarHoverOptions = {}): UseSuperbarHoverReturn => {
  const { trackEvent } = useAnalytics();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [hasTriggered, setHasTriggered] = useState(hasExpandedThisSession);
  const timeouts = useRef<{ expand?: TimeoutId; longHover?: TimeoutId }>({});

  const onAutoExpandRef = useRef(onAutoExpand);
  onAutoExpandRef.current = onAutoExpand;

  useEffect(() => {
    return () => {
      if (timeouts.current.expand) clearTimeout(timeouts.current.expand);
      if (timeouts.current.longHover) clearTimeout(timeouts.current.longHover);
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    trackEvent(AnalyticsEvent.SUPERBAR_HOVER);

    timeouts.current.longHover = setTimeout(() => {
      trackEvent(AnalyticsEvent.SUPERBAR_LONG_HOVER);
    }, LONG_HOVER_THRESHOLD_MS);

    // Auto-expand logic (if enabled, desktop only, once per session)
    if (
      autoExpandEnabled &&
      onAutoExpandRef.current &&
      !isMobile &&
      !hasTriggered
    ) {
      timeouts.current.expand = setTimeout(() => {
        markAsExpanded();
        setHasTriggered(true);
        trackEvent(AnalyticsEvent.SUPERBAR_AUTO_EXPAND_TRIGGERED);
        onAutoExpandRef.current?.();
      }, autoExpandDelaySeconds * 1000);
    }
  }, [
    trackEvent,
    autoExpandEnabled,
    autoExpandDelaySeconds,
    isMobile,
    hasTriggered,
  ]);

  const onMouseLeave = useCallback(() => {
    if (timeouts.current.expand) {
      clearTimeout(timeouts.current.expand);
      timeouts.current.expand = undefined;
    }
    if (timeouts.current.longHover) {
      clearTimeout(timeouts.current.longHover);
      timeouts.current.longHover = undefined;
    }
  }, []);

  return { onMouseEnter, onMouseLeave };
};
